import { useRef, useEffect } from "react";

export type UseAutoResizeTextareaProps = {
  value: string;
  minRows?: number;
  maxRows?: number;
  disabled: boolean;
};

export const useAutoResizeTextarea = ({
  value,
  minRows,
  maxRows,
  disabled,
}: UseAutoResizeTextareaProps) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = ref.current;

    if (!textarea || disabled) {
      return;
    }

    const originalResize = textarea.style.resize;
    const originalMinHeight = textarea.style.minHeight;
    const originalMaxHeight = textarea.style.maxHeight;

    const computedStyle = getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const borderHeight =
      parseInt(computedStyle.borderTopWidth) +
      parseInt(computedStyle.borderBottomWidth);
    const paddingHeight =
      parseInt(computedStyle.paddingTop) +
      parseInt(computedStyle.paddingBottom);

    textarea.style.resize = "none";

    if (minRows) {
      textarea.style.minHeight = `${minRows * lineHeight + borderHeight + paddingHeight}px`;
    }

    if (maxRows) {
      textarea.style.maxHeight = `${maxRows * lineHeight + borderHeight + paddingHeight}px`;
    }

    return () => {
      textarea.style.resize = originalResize;
      textarea.style.minHeight = originalMinHeight;
      textarea.style.maxHeight = originalMaxHeight;
    };
  }, [ref.current, disabled, value, minRows, maxRows]);

  useEffect(() => {
    const textarea = ref.current;

    if (!textarea || disabled || !value) {
      return;
    }

    const originalHeight = textarea.style.height;

    const computedStyle = getComputedStyle(textarea);
    const borderHeight =
      parseInt(computedStyle.borderTopWidth) +
        parseInt(computedStyle.borderBottomWidth) || 2;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;

    return () => {
      textarea.style.height = originalHeight;
    };
  }, [ref.current, disabled, value]);

  return { ref };
};
